"use client";

import {usePathname} from "next/navigation";
import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {CookieBannerBrowser} from "~/components/dataprivacy/cookies/CookieBannerBrowser";
import {CookieConsentSettings, defaultCookieSettings} from "~/components/dataprivacy/cookies/CookieBanner";
import {sendGTMEvent} from "@next/third-parties/google";

export const dynamic = "force-dynamic";

const cookieConsentLocalStorageKey = "docid-cc";

interface ClientAnalyticsContextProps {
    readonly cookieSettings: CookieConsentSettings,
    readonly isCookieBannerVisible: boolean;
    disableCookieBanner: () => void;
    hideCookieBanner: () => void;
}

/**
 * Parse cookie settings from local storage
 *
 * @param settings the users cookie settings
 */
function parseCookieSettings(settings: any): CookieConsentSettings {
    try {
        const cookieSettings = (settings) ? JSON.parse(settings) : false;
        const currentSettings: CookieConsentSettings = { ...defaultCookieSettings };
        if (cookieSettings && typeof cookieSettings === "object") {
            for (const key in cookieSettings) {
                if (typeof cookieSettings[key] === "boolean") {
                    (currentSettings as any)[key] = cookieSettings[key];
                }
            }
        }
        return currentSettings;
    } catch {
        console.log("Error parsing cookie settings", settings);
        return defaultCookieSettings;
    }
}

const ClientAnalyticsContext = createContext<ClientAnalyticsContextProps>({
    cookieSettings: defaultCookieSettings,
    isCookieBannerVisible: false,
    disableCookieBanner: () => {},
    hideCookieBanner: () => {}
});

export const ClientAnalyticsProvider = ({ dictionary, children } : { dictionary: any, children: React.ReactNode }) => {

    const pathname = usePathname();

    const [showBanner, setShowBanner] = useState<boolean>(false);
    const [disableBanner, setDisableBanner] = useState<boolean>(false);

    const [cookieSettings, setCookieSettings] = useState<CookieConsentSettings>(defaultCookieSettings);
    const [GA4ClientId, setGA4ClientId] = useState<string | null>(null);
    const [GA4SessionId, setGA4SessionId] = useState<string | null>(null);

    useEffect(() => {

        const cookieConsent = localStorage.getItem(cookieConsentLocalStorageKey);

        /**
         * Parse user cookie consent
         */
        const parsedCookieSettings = parseCookieSettings(cookieConsent);

        if(parsedCookieSettings.expirationDate >= Date.now()) {
            setCookieSettings(parsedCookieSettings);
        }

        /**
         * Show cookie banner if any consent is missing
         */
        if (!cookieConsent || parsedCookieSettings.expirationDate < Date.now()) {
            setShowBanner(true);
        }

    }, []);

    const submitCookieSettings = (settings: CookieConsentSettings) => {
        setCookieSettings(settings);
        localStorage.setItem(cookieConsentLocalStorageKey, JSON.stringify(settings));
        setShowBanner(false);
    }

    const sendPageView = () => {
        if (!pathname) return;
        sendGTMEvent({
            event: "page_view",
            page_path: pathname,
            page_title: document.title,
        })
    };

    /**
     * Call page view event on each pathname change
     */
    useEffect(() => {
        if (!pathname) return;
        sendPageView();
    }, [pathname]);

    /**
     * Initialize GA4 client id and session
     */
    useEffect(() => {
        let counter = 0;
        const iterations = 10;
        const interval = setInterval(() => {
            counter++;
            if (!window.gtag) return;
            window.gtag("get", process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID!, "client_id", (client_id: string) => {
                window.gtag("get", process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID!, "session_id", (session_id: string) => {
                    if (client_id && session_id) {
                        setGA4ClientId(client_id);
                        setGA4SessionId(session_id);
                        clearInterval(interval);
                    }
                });
            });
            if (counter >= iterations) {
                clearInterval(interval);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    /**
     * Call generic event
     */
    useEffect(() => {
        if (GA4ClientId && GA4SessionId) {
            fetch("/api/event", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    client_id: GA4ClientId,
                    session_id: GA4SessionId,
                    event_title: document.title,
                    event_location: window.location.href
                })
            }).catch((error) => {
                console.warn("Event failed:", error);
            });
        }
    }, [GA4ClientId]);

    /**
     * Provider values
     */
    const value = useMemo(() => {
        return {
            cookieSettings: cookieSettings,
            isCookieBannerVisible: showBanner,
            disableCookieBanner: () => setDisableBanner(true),
            hideCookieBanner: () => setShowBanner(false),
        };
    }, [cookieSettings, showBanner]);

    return (
        <ClientAnalyticsContext.Provider value={value}>
            {children}
            {!disableBanner && (
                <CookieBannerBrowser
                    dictionary={dictionary}
                    showBanner={showBanner}
                    settings={cookieSettings}
                    submitSettings={submitCookieSettings} />
            )}
        </ClientAnalyticsContext.Provider>
    );

}

export const useClientAnalytics = () => {
    const context = useContext(ClientAnalyticsContext);
    if (context === undefined) {
        throw new Error("useClientAnalytics must be used within an ClientAnalyticsProvider.");
    }
    return context;
};